import { lazyWithRetry } from "@/lib/lazyWithRetry";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Suspense } from "react";
import React from "react";
import { Toaster } from "react-hot-toast";
import { MainLayout } from "@/components/layout/MainLayout";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { TenantProvider } from "@/contexts/TenantContext";
import { PermissionProvider } from "@/contexts/PermissionContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleProtectedRoute from "@/components/RoleProtectedRoute";
import RoleProtectedSalesManager from "@/components/RoleProtectedSalesManager";

import {
  ReportingGuard,
  SettingsGuard,
  UserManageGuard,
  PermissionGuard,
  AdminGuard,
  SuperadminGuard,
} from "@/components/PermissionGuard";
import OnboardingProtectedRoute from "@/components/OnboardingProtectedRoute";
import { supabase, SUPABASE_AUTH_STORAGE_KEY } from "@/lib/supabaseClient";
import { getSessionBounded, SESSION_BOOT_WATCHDOG_MS } from "@/lib/sessionBoot";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import BookingConfirmation from "./features/site-survey/pages/BookingConfirmation";

// Component to handle global auth events inside router context
function AuthEventHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthSignedOut = () => {
      navigate('/login');
    };

    window.addEventListener('auth-signed-out', handleAuthSignedOut);
    return () => window.removeEventListener('auth-signed-out', handleAuthSignedOut);
  }, [navigate]);

  return null; // This component only handles events
}
import SiteSurveyV3 from "./features/site-survey/pages/SiteSurvey3";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { TasksPage } from "@/pages/Tasks";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SignContractPage } from "@/pages/SignContractPage";
// PROP-E.2: the customer's branded proposal viewer. Public, like /sign — the share token is the only
// authorisation and the customer has no account.
import ProposalSharePage from "@/pages/ProposalSharePage";
import Settings from "./pages/Settings";
import RolesPermissionsPage from "./components/settings/roles/RolesPermissionsPage";
import { ThemeTest } from "./components/ThemeTest";
import { PageLoadingSpinner } from "@/components/ui/LoadingSpinner";
import RLSTestPage from "./pages/RLSTestPage";
import { PostHogIdentificationProvider } from "@/components/PostHogIdentificationProvider";
import Impersonate from "./pages/Impersonate";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { DefaultDashboardRedirect } from "@/components/DefaultDashboardRedirect";

// Lazy load pages
const HomePage = lazyWithRetry(() =>
  import("@/pages/HomePage").then((module) => ({ default: module.HomePage }))
);
const DashboardView = lazyWithRetry(() =>
  import("@/pages/DashboardView").then((module) => ({ default: module.DashboardView }))
);
// KNK-1.14. Lazy so Leaflet and terra-draw stay out of the main bundle.
const DoorMap = lazyWithRetry(() => import("@/pages/DoorMap"));
const LiveTeamMap = lazyWithRetry(() => import("@/pages/LiveTeamMap"));
const KnockReviews = lazyWithRetry(() => import("@/pages/KnockReviews"));
const ProjectDeepLink = lazyWithRetry(() =>
  import("@/pages/ProjectDeepLink").then((module) => ({
    default: module.ProjectDeepLink,
  }))
);
const Pipeline = lazyWithRetry(() =>
  import("@/pages/Pipeline").then((module) => ({ default: module.Pipeline }))
);
const PipelineResi = lazyWithRetry(() =>
  import("@/pages/PipelineResi").then((module) => ({
    default: module.PipelineResi,
  }))
);
const Contracts = lazyWithRetry(() => import("@/pages/Contracts"));
const Login = lazyWithRetry(() =>
  import("@/pages/Login").then((module) => ({ default: module.Login }))
);
const ForgotPassword = lazyWithRetry(() =>
  import("@/pages/ForgotPassword").then((module) => ({
    default: module.ForgotPassword,
  }))
);
const ResetPassword = lazyWithRetry(() =>
  import("@/pages/ResetPassword").then((module) => ({
    default: module.ResetPassword,
  }))
);
// KNK-1.22: the page every Knock Mode invite link opens. Public: the rep has no login yet.
const JoinKnockInvite = lazyWithRetry(() =>
  import("@/pages/JoinKnockInvite").then((module) => ({ default: module.JoinKnockInvite }))
);
const NewProject = lazyWithRetry(() => import("@/pages/NewProject"));
const ProfilePage = lazyWithRetry(() => import("@/pages/ProfilePage"));
const SetupStages = lazyWithRetry(() => import("@/pages/SetupStages"));

const ChatPage = lazyWithRetry(() => import("@/pages/ChatPage").then(m => ({ default: m.ChatPage })));
const Support = lazyWithRetry(() => import("@/pages/Support"));
const SupportTickets = lazyWithRetry(() => import("@/pages/SupportTickets"));
const TicketDetail = lazyWithRetry(() => import("@/pages/TicketDetail"));
const NewTicket = lazyWithRetry(() => import("@/pages/NewTicket"));
const Schedule = lazyWithRetry(() => import("@/pages/Schedule"));
const DevRoadmap = lazyWithRetry(() => import("@/pages/DevRoadmap"));
const RoadmapEpic = lazyWithRetry(() => import("@/pages/RoadmapEpic"));
// SUB-2.11: FR63. The entry point a parent with zero sub-accounts still reaches.
// Deliberately NOT wrapped in PermissionGuard at the route: the gate is a role
// plus a server answer about THIS account, and the page owns both so the route
// and the sidebar cannot drift apart the way SUB-2.2's did.
const SubAccounts = lazyWithRetry(() => import("@/pages/SubAccounts"));
const PortfolioRollup = lazyWithRetry(() => import("@/pages/PortfolioRollup"));
import { ALL_ACCOUNTS_DASHBOARD_ID } from "@/lib/builtinDashboards";
// SD-9.1: FR-31 cost surface. Superadmin only; the database enforces it independently.
const DesignCostView = lazyWithRetry(() => import("@/pages/DesignCostView"));
const IntegrationCallback = lazyWithRetry(() => import("@/pages/IntegrationCallback"));
const Reporting = lazyWithRetry(() =>
  import("@/pages/Reporting").then((module) => ({ default: module.Reporting }))
);
const Notifications = lazyWithRetry(() => import("@/pages/Notifications"));
// COM-3.4. Self-gated on the commission_costing module: the page renders NOTHING
// for an account that was never switched on, so the route is safe to register
// unconditionally and there is no second place for that decision to drift.
const MyEarnings = lazyWithRetry(() => import("@/pages/MyEarnings"));
const CommissionPeriods = lazyWithRetry(() => import("@/pages/CommissionPeriods"));
const CommissionOverview = lazyWithRetry(() => import("@/pages/CommissionOverview"));
const CommissionMoneyHeld = lazyWithRetry(() => import("@/pages/CommissionMoneyHeld"));
const CommissionDrawsPage = lazyWithRetry(() => import("@/pages/CommissionDrawsPage"));
const CommissionGuaranteesPage = lazyWithRetry(() => import("@/pages/CommissionGuaranteesPage"));
const CommissionCancelledPage = lazyWithRetry(() => import("@/pages/CommissionCancelledPage"));
const CommissionSetup = lazyWithRetry(() => import("@/pages/CommissionSetup"));
const Redlines = lazyWithRetry(() => import("@/pages/Redlines"));
const CommercialRedlines = lazyWithRetry(() => import("@/pages/CommercialRedlines"));
const SESRedlines = lazyWithRetry(() => import("@/pages/SESRedlines"));
const AddUserPage = lazyWithRetry(() => import("@/pages/AddUserPage"));
const Tracking = lazyWithRetry(() =>
  import("@/pages/Tracking").then((module) => ({ default: module.Tracking }))
);
// SCH-5.4: cross-tenant admin pages (superadmin only).
const AdminSchIntegrations = lazyWithRetry(() =>
  import("@/pages/admin/AdminSchIntegrations").then((m) => ({
    default: m.AdminSchIntegrations,
  })),
);
// RBAC-5.3: accounts nobody can administer, and their recovery (superadmin only).
const AccountHealth = lazyWithRetry(() =>
  import("@/pages/admin/AccountHealth").then((m) => ({ default: m.AccountHealth })),
);
const AdminSchIntegrationDetail = lazyWithRetry(() =>
  import("@/pages/admin/AdminSchIntegrationDetail").then((m) => ({
    default: m.AdminSchIntegrationDetail,
  })),
);
const AuroraIframe = lazyWithRetry(() => import("@/pages/AuroraIframe"));
const MobileSupport = lazyWithRetry(() => import("@/pages/MobileSupport"));
const OnboardingPipeline = lazyWithRetry(() => import("@/pages/OnboardingPipeline"));

const FAQ = lazyWithRetry(() => import("@/pages/FAQ"));
const PlaylistManager = lazyWithRetry(() => import("@/pages/PlaylistManager"));
const VideoManager = lazyWithRetry(() => import("@/pages/VideoManager"));
const Integrations = lazyWithRetry(() => import("@/pages/Integrations"));
const AutomationBuilder = lazyWithRetry(() =>
  import("@/pages/AutomationBuilder").then((module) => ({
    default: module.AutomationBuilder,
  }))
);
const AutomationTesting = lazyWithRetry(() =>
  import("@/pages/AutomationTesting").then((module) => ({
    default: module.AutomationTesting,
  }))
);
const UpdateNotificationDemo = lazyWithRetry(() => import("@/pages/UpdateNotificationDemo"));

// Spike components (development only)
const DrawerSpike = lazyWithRetry(() =>
  import("@/spike/DrawerSpike").then((module) => ({
    default: module.DrawerSpike
  }))
);

// Software Stack Assessment pages
const PublicSoftwareStackAssessment = lazyWithRetry(() => 
  import("@/pages/PublicSoftwareStackAssessment").then((module) => ({ 
    default: module.PublicSoftwareStackAssessment 
  }))
);
const SoftwareStackResults = lazyWithRetry(() => 
  import("@/pages/SoftwareStackResults").then((module) => ({ 
    default: module.SoftwareStackResults 
  }))
);

// Booking Links pages
const PublicBookingPage = lazyWithRetry(() => import("@/pages/PublicBookingPage"));
const BookingConfirmationPage = lazyWithRetry(
  () => import("@/pages/BookingConfirmationPage")
);
const BookingTest = lazyWithRetry(() => import("@/pages/BookingTest"));
const BookingRequests = lazyWithRetry(() => import("@/pages/BookingRequests"));
const BookingLinkDiagnostic = lazyWithRetry(
  () => import("@/pages/BookingLinkDiagnostic")
);

// Customer Portal
const CustomerPortal = lazyWithRetry(() => import("@/pages/CustomerPortal"));
const CustomerPortalAuth = lazyWithRetry(() => import("@/pages/CustomerPortalAuth"));
const CustomerPortalFiles = lazyWithRetry(() => import("@/pages/CustomerPortalFiles"));

// Loading component
const LoadingFallback = () => <PageLoadingSpinner message="Loading..." />;

// Protected route with password reset check
const ProtectedRouteWithResetCheck = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [needsPasswordReset, setNeedsPasswordReset] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkUserStatus = async () => {
      try {
        /**
         * ⚠️ BOUNDED ON PURPOSE — same reason as AuthContext's fetchSession, which is the
         * outer gate. `setIsLoading(false)` only runs in the `finally` below, so an
         * unbounded await here leaves the user on `LoadingFallback` forever. Note that
         * authentication is already enforced by the `ProtectedRoute` wrapping this
         * component; what is unique here is the force_password_reset check.
         */
        const { session } = await getSessionBounded({
          getSession: () => supabase.auth.getSession(),
          storage: typeof window !== "undefined" ? window.localStorage : undefined,
          storageKey: SUPABASE_AUTH_STORAGE_KEY,
          onTimeout: (timeoutMs) => {
            console.warn(
              `[auth] getSession() did not answer within ${timeoutMs}ms in the password-reset ` +
                'check; using the persisted session. See sessionBoot.ts.'
            );
          },
        });

        if (!session) {
          navigate("/login");
          return;
        }

        // Add a small delay to ensure the session is fully initialized
        await new Promise((resolve) => setTimeout(resolve, 100));

        const { data: userData, error } = await supabase
          .from("users")
          .select("force_password_reset")
          .eq("id", session.user.id)
          .single();

        if (error) {
          navigate("/login");
          return;
        }

        if (userData?.force_password_reset === true) {
          setNeedsPasswordReset(true);
          navigate("/reset-password");
          return;
        }
      } catch (error) {
        console.error("Error checking auth:", error);
        navigate("/login");
      } finally {
        setIsLoading(false);
      }
    };

    /**
     * ⚠️ Same reasoning as AuthContext's watchdog: the `finally` above only runs once every
     * await has settled, and the `users` read below `getSession()` hangs on the same auth
     * lock. Rendering children is the right fail-open here — `ProtectedRoute` has already
     * established the user is signed in, and the only check unique to this component is
     * force_password_reset, which is a UX gate rather than a security boundary (RLS is the
     * boundary, and it still applies to every query).
     */
    const budgetMs = SESSION_BOOT_WATCHDOG_MS;
    const watchdog = setTimeout(() => {
      console.warn(
        `[auth] the password-reset check did not finish within ${budgetMs}ms; ` +
          'rendering anyway so the app cannot hang on "Loading...". See sessionBoot.ts.'
      );
      setIsLoading(false);
    }, budgetMs);

    checkUserStatus().finally(() => clearTimeout(watchdog));

    return () => clearTimeout(watchdog);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (needsPasswordReset) {
    return null;
  }

  return <>{children}</>;
};

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: '#10B981',
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: '#EF4444',
                  secondary: '#fff',
                },
              },
            }}
          />
          <AuthProvider>
          <TenantProvider>
            <PermissionProvider>
              <ThemeProvider>
                <SidebarProvider>
                  <PostHogIdentificationProvider>
                  <AuthEventHandler />
                  {/*
                    NAV-6.1: do NOT wrap <Routes> in RouteErrorBoundary (pathname-keyed).
                    That remounted MainLayout and ProtectedRouteWithResetCheck on every
                    in-app nav — sidebar and header disappeared, then the reset-check
                    showed a full-screen "Loading...". Public lazy routes still need a
                    Suspense and an error boundary; the keyed reset lives around Outlet
                    inside MainLayout so only the page remounts.
                  */}
                  <Suspense fallback={<LoadingFallback />}>
                  <ErrorBoundary>
                  <Routes>
                    {/* Public routes */}
                    <Route path="/" element={<Navigate to="/home" replace />} />
                    <Route path="/home" element={<HomePage />} />
                    <Route path="/mobile-support" element={<MobileSupport />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/sign" element={<SignContractPage />} />
                    {/* PROP-E.2. The backend's /api/v1/proposals/p/{token} 302s here, so links already
                        sent to customers land on this page rather than a raw PDF. */}
                    <Route path="/p/:token" element={<ProposalSharePage />} />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/join" element={<JoinKnockInvite />} />
                    <Route path="/join/:code" element={<JoinKnockInvite />} />
                    <Route path="/impersonate" element={<Impersonate />} />
                    <Route
                      path="/integration-callback"
                      element={<IntegrationCallback />}
                    />
                    <Route
                      path="/integrations"
                      element={<Integrations />}
                    />

                    {/* Public booking routes */}
                    <Route path="/book/:slug" element={<PublicBookingPage />} />
                    <Route
                      path="/booking-confirmation/:slug"
                      element={<BookingConfirmationPage />}
                    />
                    <Route path="/booking-test" element={<BookingTest />} />
                    <Route
                      path="/booking-diagnostic/:slug"
                      element={<BookingLinkDiagnostic />}
                    />

                    {/* Public AI audit assessment routes */}
                    <Route path="/ai-audit" element={<PublicSoftwareStackAssessment />} />
                    <Route path="/ai-audit/results" element={<SoftwareStackResults />} />

                    {/* Public customer portal routes */}
                    <Route path="/customer/:projectId" element={<CustomerPortal />} />
                    <Route path="/customer/:projectId/auth" element={<CustomerPortalAuth />} />
                    <Route path="/customer/:projectId/portal" element={<CustomerPortalFiles />} />

                    {/* Protected routes under /app */}
                    <Route
                      path="/app"
                      element={
                        <ProtectedRoute>
                          <ProtectedRouteWithResetCheck>
                            <MainLayout />
                          </ProtectedRouteWithResetCheck>
                        </ProtectedRoute>
                      }
                    >
                      <Route
                        index
                        element={<DefaultDashboardRedirect />}
                      />
                      <Route path="dashboard" element={<DefaultDashboardRedirect />} />
                      {/* SUB-5.10 · FR44. The All Accounts overview, inside the
                          Dashboards surface because that is where people look
                          for a dashboard, and still A ROUTE rather than a
                          `dashboards` row (D51), so `clone_tenant_config` can
                          never copy it into a client account. A static segment
                          outranks `:id`, and the id is a slug so it cannot
                          collide with a real `dashboards.id`.
                          ⚠️ It guards ITSELF: outside merged scope
                          `PortfolioRollup` redirects rather than explaining, so
                          a hand-typed URL discloses nothing about whether a
                          hierarchy exists (FR19, SUB-2.11). */}
                      <Route
                        path={`dashboards/${ALL_ACCOUNTS_DASHBOARD_ID}`}
                        element={<PortfolioRollup />}
                      />
                      <Route path="dashboards/:id" element={<DashboardView />} />
                      {/* KNK-1.14 · FR-12. DoorMap gates itself on Knock Mode and
                          door.view / territory.manage; the database gates every read. */}
                      <Route path="doors" element={<DoorMap />} />
                      {/* KNK-1.57 · FR-33a. Gates itself on Knock Mode and field.live_view;
                          live_team_map() refuses everyone else and logs every view. */}
                      <Route path="live-team" element={<LiveTeamMap />} />
                      {/* KNK-1.44 · FR-35. Gates itself on Knock Mode and knock.review;
                          knock_review_queue() refuses everyone else. */}
                      <Route path="knock-reviews" element={<KnockReviews />} />
                      <Route
                        path="roadmap"
                        element={
                          <PermissionGuard permissions={["system.override"]}>
                            <DevRoadmap />
                          </PermissionGuard>
                        }
                      />
                      {/* RBAC-5.3 · NFR-6b. degraded_accounts() refuses anyone who is not a superadmin. */}
                      <Route
                        path="account-health"
                        element={
                          <PermissionGuard permissions={["system.override"]}>
                            <AccountHealth />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="roadmap/:epicId"
                        element={
                          <PermissionGuard permissions={["system.override"]}>
                            <RoadmapEpic />
                          </PermissionGuard>
                        }
                      />
                      {/* SUB-2.11 · FR63. Reachable independently of the account
                          picker, which hides itself at one accessible account and
                          so cannot be the entry point for a parent that has no
                          sub-accounts yet. SubAccounts guards itself. */}
                      <Route path="sub-accounts" element={<SubAccounts />} />
                      {/* SUB-5.1 · FR44. The All Accounts dashboard, a route
                          rather than a `dashboards` row (D51), so it can never be
                          cloned into a client account by `clone_tenant_config`
                          and FR45 stays true by construction. ⚠️ It guards
                          ITSELF and behaves as though the route does not exist
                          for anyone without merged scope, so a hand-typed URL
                          discloses nothing about the hierarchy (FR19, SUB-2.11). */}
                      <Route path="portfolio" element={<PortfolioRollup />} />
                      <Route
                        path="design-cost"
                        element={
                          <PermissionGuard permissions={["system.override"]}>
                            <DesignCostView />
                          </PermissionGuard>
                        }
                      />

                      <Route
                        path="redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "project.view_all",
                              "project.view_team",
                            ]}
                          >
                            <Redlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="commercial-redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_commercial_pipeline",
                              "view_projects",
                              "project.view_all",
                            ]}
                          >
                            <CommercialRedlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="ses-redlines"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "project.view_all",
                              "project.view_team",
                            ]}
                          >
                            <SESRedlines />
                          </PermissionGuard>
                        }
                      />
                      <Route
                        path="tasks"
                        element={
                          <PermissionGuard
                            permissions={[
                              "view_projects",
                              "manage_projects",
                              "edit_projects",
                            ]}
                          >
                            <TasksPage />
                          </PermissionGuard>
                        }
                      />
                      {/*
                        SUB-5.23. ⚠️ **`/app/projects` (the LIST) IS DELETED.
                        `/app/projects/:projectId` IS NOT, AND THE DIFFERENCE
                        MATTERS.** These were always SIBLING routes rather than a
                        parent and child, so removing the list leaves the deep
                        link untouched — which is essential, because
                        `/app/projects/:projectId` is the canonical project URL:
                        automation emails build it (`automationSimulationService`),
                        `ProjectModal` hands it out as the share link,
                        `ActivityModal` navigates to it, and
                        `VariablePickerButton` documents it as `project_link`.
                        Deleting it would break links already sitting in
                        customers' inboxes.
                      */}
                      <Route
                        path="projects/:projectId"
                        element={<ProjectDeepLink />}
                      />
                      <Route path="pipeline" element={<Pipeline />} />
                      <Route path="pipeline-resi" element={<PipelineResi />} />
                      <Route path="aurora" element={<AuroraIframe />} />
                      <Route
                        path="reporting"
                        element={
                          <ReportingGuard>
                            <Reporting />
                          </ReportingGuard>
                        }
                      />
                      <Route
                        path="contracts"
                        element={
                          <RoleProtectedRoute>
                            <Contracts />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="tracking"
                        element={
                          <PermissionGuard permission="reporting.admin">
                            <Tracking />
                          </PermissionGuard>
                        }
                      />
                      {/* SCH-5.4: cross-tenant admin (superadmin only). The
                          page-level SuperadminGuard double-checks via
                          system.override permission. */}
                      <Route
                        path="admin/integrations/subcontractorhub"
                        element={
                          <SuperadminGuard>
                            <AdminSchIntegrations />
                          </SuperadminGuard>
                        }
                      />
                      <Route
                        path="admin/integrations/subcontractorhub/:tenantId"
                        element={
                          <SuperadminGuard>
                            <AdminSchIntegrationDetail />
                          </SuperadminGuard>
                        }
                      />
                      <Route
                        path="settings"
                        element={
                          <SettingsGuard>
                            <Settings />
                          </SettingsGuard>
                        }
                      />
                      {/* RBAC-3.1. NOT behind SettingsGuard: that redirects anyone without
                          manage_settings, and AC 2 wants a calm no-access surface. The page gates
                          itself on roles.administer (rolesScreenAccess), never manage_roles. */}
                      <Route path="settings/roles" element={<RolesPermissionsPage />} />
                      <Route
                        path="theme-test"
                        element={
                          <RoleProtectedRoute>
                            <ThemeTest />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="update-notification-demo"
                        element={
                          <RoleProtectedRoute>
                            <UpdateNotificationDemo />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="drawer-spike"
                        element={
                          <RoleProtectedRoute>
                            <DrawerSpike />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="automation-builder/:automationId"
                        element={
                          <RoleProtectedRoute>
                            <AutomationBuilder />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="automation-testing"
                        element={
                          <AdminGuard>
                            <AutomationTesting />
                          </AdminGuard>
                        }
                      />
                      <Route path="new-project" element={<NewProject />} />
                      <Route path="profile" element={<ProfilePage />} />
                      <Route
                        path="add-user"
                        element={
                          <UserManageGuard>
                            <AddUserPage />
                          </UserManageGuard>
                        }
                      />
                      <Route path="setup-stages" element={<SetupStages />} />
                      <Route path="chat" element={<ChatPage />} />
                      <Route path="support" element={<Support />} />
                      <Route
                        path="support-tickets"
                        element={<SupportTickets />}
                      />
                      <Route
                        path="support-tickets/new"
                        element={<NewTicket />}
                      />
                      <Route
                        path="support-tickets/:ticketId"
                        element={
                          <ErrorBoundary>
                            <TicketDetail />
                          </ErrorBoundary>
                        }
                      />
                      <Route path="faq" element={<FAQ />} />
                      <Route
                        path="playlist-manager"
                        element={
                          <RoleProtectedRoute>
                            <PlaylistManager />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route
                        path="video-manager"
                        element={
                          <RoleProtectedRoute>
                            <VideoManager />
                          </RoleProtectedRoute>
                        }
                      />
                      <Route path="notifications" element={<Notifications />} />

                      {/* COM-3.4. The rep earnings surface COM-2.8 said it
                          could not render a blocked badge without. */}
                      <Route path="earnings" element={<MyEarnings />} />

                      {/* COM-7.2. The operator's pay period screen. Read-only:
                          approving is COM-7.3 and paying is COM-7.5, and they
                          are different authorities on purpose.

                          ⚠️ NOT under `earnings`. That route is gated on the
                          rollout switch alone so a rep sees their own pay; this
                          one needs `commission.view`, and the screen itself
                          refuses rather than rendering an empty list, because an
                          unauthorised caller gets zero rows and not an error. */}
                      {/* NAV-5.1. The Commission area. Pay periods already lived
                          here and do not move. Overview remounts the existing
                          health card until NAV-5.2. Each destination self-gates
                          the same way the sidebar does. */}
                      <Route path="commission" element={<CommissionOverview />} />
                      <Route path="commission/periods" element={<CommissionPeriods />} />
                      <Route path="commission/periods/:periodId" element={<CommissionPeriods />} />
                      <Route path="commission/money-held" element={<CommissionMoneyHeld />} />
                      <Route path="commission/draws" element={<CommissionDrawsPage />} />
                      <Route path="commission/guarantees" element={<CommissionGuaranteesPage />} />
                      <Route path="commission/cancelled" element={<CommissionCancelledPage />} />
                      <Route path="commission/setup" element={<CommissionSetup />} />
                      <Route path="commission/setup/:section" element={<CommissionSetup />} />

                      {/* Scheduler routes */}
                      <Route
                        path="scheduler"
                        element={<Navigate to="appointments" replace />}
                      />
                      <Route
                        path="scheduler/appointments"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/event-management"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/availability"
                        element={<Schedule />}
                      />
                      <Route
                        path="scheduler/booking-links"
                        element={
                          <AdminGuard>
                            <Schedule />
                          </AdminGuard>
                        }
                      />
                      <Route
                        path="scheduler/booking-links/requests"
                        element={
                          <AdminGuard>
                            <BookingRequests />
                          </AdminGuard>
                        }
                      />

                      {/* Redirect old events-management path */}
                      <Route
                        path="events-management"
                        element={
                          <Navigate to="scheduler/event-management" replace />
                        }
                      />

                      {/* Site Survey Routes */}
                      <Route path="sitesurvey" element={<SiteSurveyV3 />} />
                      <Route
                        path="sitesurvey/booking-confirmation/:id"
                        element={<BookingConfirmation />}
                      />
                      <Route
                        path="booking-confirmation"
                        element={<BookingConfirmation />}
                      />

                      {/* Only redirect unknown routes that are not onboarding */}
                      <Route
                        path="onboarding"
                        element={
                          <OnboardingProtectedRoute>
                            <OnboardingPipeline />
                          </OnboardingProtectedRoute>
                        }
                      />
                      <Route
                        path="rls-test"
                        element={
                          <SuperadminGuard>
                            <RLSTestPage />
                          </SuperadminGuard>
                        }
                      />
                      <Route
                        path=":rest"
                        element={<Navigate to="/app/dashboard" replace />}
                      />
                    </Route>

                    {/* Redirect old routes to new /app prefixed routes */}
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/app/dashboard" replace />}
                    />
                    <Route
                      path="/redlines"
                      element={<Navigate to="/app/redlines" replace />}
                    />
                    <Route
                      path="/projects/:projectId"
                      element={
                        <Navigate to="/app/projects/:projectId" replace />
                      }
                    />
                    <Route
                      path="/pipeline"
                      element={<Navigate to="/app/pipeline" replace />}
                    />
                    <Route
                      path="/pipeline-resi"
                      element={<Navigate to="/app/pipeline-resi" replace />}
                    />
                    <Route
                      path="/empower"
                      element={<Navigate to="/app/empower" replace />}
                    />
                    <Route
                      path="/aurora"
                      element={<Navigate to="/app/aurora" replace />}
                    />
                    <Route
                      path="/reporting"
                      element={<Navigate to="/app/reporting" replace />}
                    />
                    <Route
                      path="/contracts"
                      element={<Navigate to="/app/contracts" replace />}
                    />
                    <Route
                      path="/settings"
                      element={<Navigate to="/app/settings" replace />}
                    />
                    <Route
                      path="/new-project"
                      element={<Navigate to="/app/new-project" replace />}
                    />
                    <Route
                      path="/profile"
                      element={<Navigate to="/app/profile" replace />}
                    />
                    <Route
                      path="/setup-stages"
                      element={<Navigate to="/app/setup-stages" replace />}
                    />
                    <Route
                      path="/support"
                      element={<Navigate to="/app/support" replace />}
                    />
                    <Route
                      path="/support-tickets"
                      element={<Navigate to="/app/support-tickets" replace />}
                    />
                    <Route
                      path="/support-tickets/new"
                      element={
                        <Navigate to="/app/support-tickets/new" replace />
                      }
                    />
                    <Route
                      path="/support-tickets/:ticketId"
                      element={
                        <Navigate to="/app/support-tickets/:ticketId" replace />
                      }
                    />
                    <Route
                      path="/scheduler"
                      element={<Navigate to="/app/scheduler" replace />}
                    />
                    <Route
                      path="/scheduler/*"
                      element={<Navigate to="/app/scheduler" replace />}
                    />
                    <Route
                      path="/events-management"
                      element={<Navigate to="/app/events-management" replace />}
                    />
                    <Route
                      path="/sitesurvey"
                      element={<Navigate to="/app/sitesurvey" replace />}
                    />
                    <Route
                      path="/sitesurvey/booking-confirmation/:id"
                      element={
                        <Navigate
                          to="/app/sitesurvey/booking-confirmation/:id"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/booking-confirmation"
                      element={
                        <Navigate to="/app/booking-confirmation" replace />
                      }
                    />
                    <Route
                      path="/faq"
                      element={<Navigate to="/app/faq" replace />}
                    />

                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                  </ErrorBoundary>
                  </Suspense>
                  </PostHogIdentificationProvider>
                </SidebarProvider>
              </ThemeProvider>
            </PermissionProvider>
          </TenantProvider>
        </AuthProvider>
      </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
